import { useMutation } from "@apollo/client";
import { useOrderById } from "./useOrderById";
import {
  CreateOrderAttachmentDocument,
  DeleteOrderAttachmentDocument,
  GetOrderByIdDocument,
} from "../../gql/generated/graphql";
import { usePresignedFileUpload } from "../files/usePresignedFileUpload";

interface Props {
  orderId: string;
}

// Files the dentist attaches to a draft order
export const useOrderFiles = ({ orderId }: Props) => {
  const {
    order,
    isLoading,
    error,
    isEditable,
    refetch: refetchOrder,
  } = useOrderById({ orderId });

  const [createOrderAttachment] = useMutation(CreateOrderAttachmentDocument);
  const [deleteOrderAttachment] = useMutation(DeleteOrderAttachmentDocument, {
    refetchQueries: [GetOrderByIdDocument],
  });
  const { uploadFile } = usePresignedFileUpload();

  const uploadOrderFile = async (file: File) => {
    if (!isEditable) {
      throw new Error("Order is not editable");
    }

    const res = await createOrderAttachment({
      variables: {
        orderId,
        filename: file.name,
      },
    });

    if (res.errors || !res.data?.createOrderAttachment) {
      throw new Error("Error creating order attachment");
    }

    try {
      await uploadFile(res.data.createOrderAttachment, file);
    } catch (error) {
      // Otherwise the draft file created above would stay on the order, hidden
      await deleteOrderAttachment({
        variables: { orderId, fileId: res.data.createOrderAttachment.fileId },
      }).catch(cleanupError =>
        console.error(
          "Error while removing the draft order attachment",
          cleanupError
        )
      );
      throw error;
    }

    // The file is saved at this point: a failed refresh must not report the upload as failed
    await refetchOrder().catch(error =>
      console.error("Error while refreshing the order files", error)
    );
  };

  const removeOrderFile = async (fileId: string) => {
    if (!isEditable) {
      throw new Error("Order is not editable");
    }

    await deleteOrderAttachment({
      variables: {
        orderId,
        fileId,
      },
    });
  };

  return {
    files: order?.files ?? [],
    isLoading,
    error,
    uploadOrderFile,
    removeOrderFile,
    refetchOrder,
  };
};
